import config from '@/app.config';
import type { TFlowItemFrontend } from '@/types/common';
import { editorValueToMarkdown } from '@/utils/editor-conversion';

function measureWithContainer(html: string, maxWidth: number): number {
	const tempContainer = document.createElement('div');
	tempContainer.style.position = 'absolute';
	tempContainer.style.visibility = 'hidden';
	tempContainer.style.width = `${maxWidth}px`;
	tempContainer.style.font = '16px/22px Inter, sans-serif';
	tempContainer.style.margin = '0';
	tempContainer.style.padding = '0';
	tempContainer.style.border = 'none';
	tempContainer.style.wordBreak = 'break-word';
	tempContainer.style.whiteSpace = 'normal';
	tempContainer.style.overflow = 'hidden';
	tempContainer.innerHTML = html;
	document.body.appendChild(tempContainer);
	const height = tempContainer.scrollHeight;
	document.body.removeChild(tempContainer);
	return height;
}

const markdownRules: [RegExp, string][] = [
	[/^### (.*$)/gim, '<h3>$1</h3>'],
	[/^## (.*$)/gim, '<h2>$1</h2>'],
	[/^# (.*$)/gim, '<h1>$1</h1>'],
	[/\*\*(.*?)\*\*/g, '<strong>$1</strong>'],
	[/\*(.*?)\*/g, '<em>$1</em>'],
	[/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>'],
	[/`([^`]+)`/g, '<code>$1</code>'],
	[/^\* (.*$)/gim, '<li>$1</li>'],
	[/^- (.*$)/gim, '<li>$1</li>'],
	[/^\d+\. (.*$)/gim, '<li>$1</li>'],
	[/\n\n/g, '</p><p>'],
	[/\n/g, '<br>']
];

function applyMarkdownReplacements(content: string): string {
	return markdownRules.reduce(
		(acc, [pattern, replacement]) => acc.replace(pattern, replacement),
		content
	);
}

function measureHeight(content: string, isBodyView: boolean, width: number) {
	if (isBodyView) {
		const htmlContent = applyMarkdownReplacements(content || '');
		return measureWithContainer(`<p>${htmlContent}</p>`, width);
	}
	return measureWithContainer(`<p>${content}</p>`, width);
}

const HEADER_HEIGHT = 46;
const CONTENT_PADDING_TOP = 40;
const CONTENT_PADDING_BOTTOM = 20;
const EXTRA_BOTTOM_SPACING = 8;
const READ_MORE_BUTTON_HEIGHT = 24;
const READ_MORE_SPACING = 16;

function computeHeights(content: string, isBodyView: boolean) {
	const contentWidth = config.view.extended.flowCard.width - 48;
	const contentHeight = measureHeight(content, isBodyView, contentWidth);
	const maxAllowedHeight = isBodyView
		? config.view.extended.flowCard.bodyViewHeight
		: config.view.extended.flowCard.height;
	const availableContentHeight =
		maxAllowedHeight -
		HEADER_HEIGHT -
		CONTENT_PADDING_TOP -
		CONTENT_PADDING_BOTTOM;

	return {
		contentHeight,
		contentWidth,
		maxAllowedHeight,
		availableContentHeight
	};
}

export function getCompactCardHeight(): number {
	if (typeof document === 'undefined') {
		return 70;
	}
	const styles = getComputedStyle(document.documentElement);
	const rootFontSize = Number.parseFloat(styles.fontSize) || 16;
	const raw = styles.getPropertyValue('--mantine-scale').trim();
	const scale = raw ? Number.parseFloat(raw) : 1;
	return Math.round(4.375 * rootFontSize * scale);
}

export const getCardHeight = (
	item: TFlowItemFrontend,
	collapsedMap: Record<string, boolean>,
	changedViewCards: Record<string, boolean>
): number => {
	const collapsed = collapsedMap[item.source.idn] ?? false;
	const isBodyView = changedViewCards[item.source.idn] ?? false;

	if (!collapsed) {
		return getCompactCardHeight();
	}

	const content = isBodyView
		? editorValueToMarkdown(item.source.body)
		: (item.source.description ?? '');
	const { contentHeight, maxAllowedHeight, availableContentHeight } =
		computeHeights(content, isBodyView);

	const needsReadMore = contentHeight > availableContentHeight;

	const baseContentHeight = needsReadMore
		? availableContentHeight - READ_MORE_SPACING - READ_MORE_BUTTON_HEIGHT
		: contentHeight;

	const dynamicHeight =
		HEADER_HEIGHT +
		CONTENT_PADDING_TOP +
		baseContentHeight +
		(needsReadMore ? READ_MORE_SPACING + READ_MORE_BUTTON_HEIGHT : 0) +
		CONTENT_PADDING_BOTTOM +
		EXTRA_BOTTOM_SPACING;

	const minHeight =
		HEADER_HEIGHT +
		CONTENT_PADDING_TOP +
		CONTENT_PADDING_BOTTOM +
		EXTRA_BOTTOM_SPACING;

	if (dynamicHeight < maxAllowedHeight) {
		return Math.max(dynamicHeight, minHeight);
	}
	return maxAllowedHeight;
};

export const truncateContentForReadMore = (
	content: string,
	maxHeight: number,
	isBodyView: boolean,
	contentWidth: number
): string => {
	if (!content) {
		return content;
	}

	const measure = (txt: string) => measureHeight(txt, isBodyView, contentWidth);

	if (measure(content) <= maxHeight) {
		return content;
	}

	const words = content.split(' ');
	let low = 0;
	let high = words.length;
	let truncated = `${words[0]}...`;

	while (low <= high) {
		const mid = Math.floor((low + high) / 2);
		const candidate = `${words.slice(0, mid).join(' ')}...`;
		if (measure(candidate) <= maxHeight) {
			truncated = candidate;
			low = mid + 1;
		} else {
			high = mid - 1;
		}
	}

	return truncated;
};

export const needsReadMoreButton = (
	item: TFlowItemFrontend,
	collapsedMap: Record<string, boolean>,
	changedViewCards: Record<string, boolean>
): boolean => {
	const collapsed = collapsedMap[item.source.idn] ?? false;
	if (!collapsed) {
		return false;
	}

	const isBodyView = changedViewCards[item.source.idn] ?? false;
	const content = isBodyView
		? editorValueToMarkdown(item.source.body)
		: (item.source.description ?? '');
	const { contentHeight, availableContentHeight } = computeHeights(
		content,
		isBodyView
	);

	return contentHeight > availableContentHeight;
};
